import type { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';
import type {
  HotelSupplementFormData,
  HotelSupplementPricingFormData,
} from '@/types/benefits/hotel/HotelSupplementSchema';
import type { Translator } from '@/types/translator.type';
import type {
  PastedNamedSupplementTariff,
  PastedSupplementTariff,
} from '@/utils/benefits/hotel/excelPaste/parseSupplementTariffBlock';
import type { ChildBandsCount } from '@/utils/benefits/hotel/excelPaste/types';
import type { ExcelPasteRecapRow } from './ExcelPastePanel';
import {
  buildLabelAndPeriodsRecap,
  buildPastedFormPeriods,
  ChildAges,
  formatRecapAmount,
  FormPeriod,
  getPeriodFieldNames,
  getPersonPriceColumns,
} from './pasteFormHelpers';

export type SupplementCard = HotelSupplementFormData;
export type SupplementTariff = HotelSupplementPricingFormData;

const hasValue = (value: unknown) =>
  value !== undefined && value !== null && value !== '';

/** A supplement tariff added but never filled: no label, no date, no price. */
export const isBlankSupplementTariff = (tariff: SupplementTariff) =>
  !tariff.name?.trim() &&
  (tariff.periods ?? []).every(
    (period) => !period.startDate && !period.endDate,
  ) &&
  !hasValue(tariff.pricing?.adultPrice) &&
  !hasValue(tariff.pricing?.adolescentPrice) &&
  !hasValue(tariff.pricing?.childPrice);

/** A supplement added but never filled: no name and only blank tariffs. */
export const isBlankSupplement = (supplement: SupplementCard) =>
  !supplement.name?.trim() &&
  (supplement.pricingList ?? []).every(isBlankSupplementTariff);

/** The supplement tariff once the pasted label, periods and price are written. */
export function mergePastedSupplementTariff(
  current: SupplementTariff | undefined,
  pasted: PastedSupplementTariff,
): SupplementTariff {
  return {
    ...current,
    name: pasted.name,
    periods: buildPastedFormPeriods(
      current?.periods as FormPeriod[] | undefined,
      pasted.periods,
    ),
    pricing: { ...current?.pricing, ...pasted.pricing },
  } as SupplementTariff;
}

/** Field names a supplement paste fills, for the highlight. */
export function getPastedSupplementFieldNames(
  supplementPath: string,
  tariffIndex: number,
  pasted: PastedSupplementTariff,
): string[] {
  const tariffPrefix = `${supplementPath}.pricingList.${tariffIndex}`;
  return [
    `${supplementPath}.name`,
    `${tariffPrefix}.name`,
    ...getPeriodFieldNames(`${tariffPrefix}.periods`, pasted.periods.length),
    `${tariffPrefix}.pricing.adultPrice`,
    `${tariffPrefix}.pricing.adolescentPrice`,
    `${tariffPrefix}.pricing.childPrice`,
  ];
}

/**
 * Recap of a supplement paste: supplement name and pension types, then the
 * tariff label, periods and per-person price.
 */
export function buildSupplementRecap(
  current: {
    supplementName?: string | null;
    pensionTypes: HotelPensionType[];
    tariff?: SupplementTariff;
  },
  pasted: PastedNamedSupplementTariff,
  {
    childBandsCount,
    childAges,
    formatPensionCodes,
  }: {
    childBandsCount: ChildBandsCount;
    childAges: ChildAges;
    formatPensionCodes: (pensionTypes: string[]) => string;
  },
  t: Translator,
): ExcelPasteRecapRow[] {
  return [
    {
      key: 'supplementName',
      label: t('hotels.pasteFromExcel.fields.supplementName'),
      current: current.supplementName ?? '',
      pasted: pasted.supplementName,
    },
    {
      key: 'pensionTypes',
      label: t('hotels.pasteFromExcel.fields.supplementPensionTypes'),
      current: formatPensionCodes(current.pensionTypes),
      pasted: formatPensionCodes(pasted.pensionTypes),
    },
    ...buildLabelAndPeriodsRecap(
      { name: current.tariff?.name, periods: current.tariff?.periods },
      pasted,
      t,
    ),
    ...getPersonPriceColumns(childBandsCount, childAges, t).map(
      ({ field, label }) => ({
        key: `price-${field}`,
        label: t('hotels.pasteFromExcel.fields.supplementPrice', {
          person: label,
        }),
        current: formatRecapAmount(current.tariff?.pricing?.[field]),
        pasted: formatRecapAmount(pasted.pricing[field]),
      }),
    ),
  ];
}
